import * as Net from '_pixiSrc/net/ajax';
import { Utils } from '_pixiSrc/Utils';
import { APP } from '_pixiSrc/globals';
import GameData from '../ui/GameData';
import { getUserData, setUserData } from './Session';
import {
    GAME_EVENT_URL,
    GAME_PROPERTY_KEY,
    ERROR,
    DEFAULT_USER_DATA,
} from '../Constants';

/**
 * @class Requests
 * A static class that implements the query logic from the server and receives replies
 */
class Requests {
    /**
   * @name checkResponse
   * @param response
   * @description check response is ok or not, show error if response in not ok
   */
    static checkResponse(response) {
        const ok = response ? !response.isError : false;
        let errorId = '';
        let reload = true;
        let buttonText = 'RELOAD';

        if (!ok) {
            errorId = ERROR.NETWORK;
        }

        if (response && response.data && response.data.error) {
            switch (response.data.errorCode) {
                case 101:
                    errorId = ERROR.NOT_FOUND;
                    break;
                case 102:
                    errorId = ERROR.SOMETHING_WENT_WRONG;
                    break;
                case 103:
                    errorId = ERROR.INVALID_GAME_EVENT;
                    break;
                case 104:
                    errorId = ERROR.NO_SESSION_DATA;
                    break;
                case 105:
                    errorId = ERROR.SPIN_NOT_ALLOWED;
                    break;
                case 106:
                    errorId = ERROR.SPIN_NOT_ALLOWED_NO_GAME_CLICK;
                    break;
                case 107:
                    errorId = ERROR.SPIN_FAILED;
                    reload = false;
                    buttonText = 'OK';
                    break;
                case 108:
                    errorId = ERROR.GAME_NOT_FOUND;
                    break;
                case 109:
                    errorId = ERROR.UNRECOGNIZED_USER;
                    break;
                case 110:
                    errorId = ERROR.NOT_ENOUGH_BALANCE;
                    reload = false;
                    buttonText = 'OK';
                    break;
                case 111:
                    errorId = ERROR.GAMBLE_NOT_ACTIVE;
                    reload = false;
                    buttonText = 'OK';
                    break;
                case 112:
                    errorId = ERROR.NO_WINS_TO_COLLECT;
                    reload = false;
                    buttonText = 'OK';
                    break;
                case 113:
                    errorId = ERROR.INVALID_CLIENT;
                    break;
                case 114:
                    errorId = ERROR.INVALID_GAMBLE_CARD;
                    reload = false;
                    buttonText = 'OK';
                    break;
                default:
                    errorId = ERROR.NETWORK;
            }
        }

        if (!ok) {
            APP.displayGlobalError(errorId, response, reload, buttonText);
        }

        return ok;
    }

    /**
   * @name increaseBet
   * @description increase bet amount
   */
    static increaseBet(token, gameId, clientID) {
        return new Promise((resolve, reject) => {
            const params = {
                token,
                gameId,
                clientID,
            };
            Net.POST(GAME_EVENT_URL.INCREASEBET, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) {
                    resolve(response.data);
                }
            });
        });
    }

    /**
   * @name decreaseBet
   * @description decrease bet amount
   */
    static decreaseBet(token, gameId, clientID) {
        return new Promise((resolve, reject) => {
            const params = {
                token,
                gameId,
                clientID,
            };
            Net.POST(GAME_EVENT_URL.DECREASEBET, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) {
                    resolve(response.data);
                }
            });
        });
    }

    /**
   * @name maxBet
   * @description set maximum bet amount
   */
    static maxBet(token, gameId, clientID) {
        return new Promise((resolve, reject) => {
            const params = {
                token,
                gameId,
                clientID,
            };
            Net.POST(GAME_EVENT_URL.MAXBET, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) {
                    resolve(response.data);
                }
            });
        });
    }

    /**
   * @name spin
   * @description get game data when user spin
   */
    static spin() {
        const params = getUserData();
        // if (params.wallet < params.currentBet * 10) {
        //     params.wallet = DEFAULT_USER_DATA.wallet;
        // }
        return new Promise((resolve, reject) => {
            let url = GAME_EVENT_URL.SPIN;
            const isAutoSpin = GameData.getProperty(GAME_PROPERTY_KEY.AUTOSPIN);
            const isTurboChecked = GameData.getProperty(GAME_PROPERTY_KEY.TURBOCHECKED);
            if (isAutoSpin && isTurboChecked) {
                url = `${url}/?isAutoSpin=true&isTurbo=true`;
            } else if (isAutoSpin || isTurboChecked) {
                if (isAutoSpin && !isTurboChecked) {
                    url = `${url}/?isAutoSpin=true`;
                } else if (!isAutoSpin && isTurboChecked) {
                    url = `${url}/?isTurbo=true`;
                }
            }
            Net.POST(url, params, (res, error, xmlhttp) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) {
                    resolve(response.data);
                } else {
                    reject(response);
                }
            });
        });
    }

    /**
   * @name startingParams
   * @description get game data
   */
    static startingParams(clientID) {
        let params = DEFAULT_USER_DATA;
        const data = getUserData();
        if (data && data.gameId && data.gameId !== '') {
            params = data;
        } else {
            params.clientID = clientID;
            setUserData(params);
        }

        return new Promise((resolve, reject) => {
            // send casino params when found in url
            Net.POST(GAME_EVENT_URL.GAME_CLICK, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) {
                    resolve(response.data);
                }
            });
        });
    }

    /**
   * @name gamble
   * @description get gamble data with pickedCard
   */
    static gambleGame(token, gameId, clientID, pickedCard) {
        return new Promise((resolve, reject) => {
            const params = {
                token,
                gameId,
                clientID,
                pickedCard,
            };
            Net.POST(GAME_EVENT_URL.GAMBLE_CARD, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) {
                    resolve(response.data);
                } else {
                    reject();
                }
            });
        });
    }

    /**
   * @name gamble
   * @description add win amount into wallet balance
   */
    static takeWin(token, gameId, clientID) {
        return new Promise((resolve, reject) => {
            const params = {
                token,
                gameId,
                clientID,
            };
            Net.POST(GAME_EVENT_URL.TAKEWIN, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) { resolve(response.data); }
            });
        });
    }

    /**
   * @name updateEvent
   * @description send mode changing request
   */
    static updateEvent(token, gameId, clientID, eventData) {
        return new Promise((resolve, reject) => {
            const params = {
                token,
                gameId,
                clientID,
                ...eventData,
            };
            Net.POST(GAME_EVENT_URL.UPDATE_EVENT, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) { resolve(response.data); }
            });
        });
    }

    static spinGift(bet, linecount, test) {
        return new Promise((resolve, reject) => {
            const params = {
                token,
                bet,
                linecount,
            };
            if (test) { params.test = test; }
            Net.GET(`${SERVER_URL}onlinecasino/giftSpin`, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) { resolve(response); }
            });
        });
    }

    static balance() {
        return new Promise((resolve, reject) => {
            const params = {
                token,
            };
            Net.GET(`${SERVER_URL}onlinecasino/common.asmx/getBalance`, params, (res) => {
                const response = JSON.parse(res);
                if (Requests.checkResponse(response)) {
                    resolve(response.balance);
                }
            });
        });
    }
}

export default Requests;
